import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SpeechTextAnalyticsApi } from "@genesys/cloud-frontend/apis/SpeechTextAnalyticsApi";
import { normalize, schema } from "normalizr";
import { ListedTopic } from "@genesys/cloud-frontend/dist/mjs/models/ListedTopic";
import { RootState } from "app/store";
import { API_CONFIGURATION } from "util/apiUtils";
import { fetchPagesWithQuery } from "../common/paginationUtils";

const PAGE_SIZE = 500;
const NUM_OF_PAGES_TO_FETCH = 2;

const normalizedTopics = new schema.Entity("topic", {
    entities: [new schema.Entity("listedTopics")],
});

type TopicsState = {
    data: {
        topics: Record<string, ListedTopic>;
        rejectedTopicIds: Array<string>;
    };
};

export const fetchTopics = createAsyncThunk("topics/fetchTopics", async () => {
    const staApi = new SpeechTextAnalyticsApi(API_CONFIGURATION);
    const getTopics = (requestData: {
        pageSize: number;
        pageNumber: number;
    }) => {
        return staApi.getSpeechandtextanalyticsTopics(requestData);
    };

    // QM-20062: only fetch 1000 entries max for now.
    return await fetchPagesWithQuery(
        getTopics,
        {}, // no other query parameters needed
        PAGE_SIZE,
        NUM_OF_PAGES_TO_FETCH
    ).then((entities) => {
        return { entities };
    });
});

export const fetchTopicsByName = createAsyncThunk(
    "topics/fetchTopicsByName",
    async (topicName: string) => {
        const staApi = new SpeechTextAnalyticsApi(API_CONFIGURATION);
        const getTopicsByName = (requestData: {
            pageSize: number;
            pageNumber: number;
            name: string;
        }) => {
            return staApi.getSpeechandtextanalyticsTopics(requestData);
        };

        return await fetchPagesWithQuery(
            getTopicsByName,
            { name: topicName },
            PAGE_SIZE,
            1
        ).then((entities) => {
            return { entities };
        });
    }
);

const initialState: TopicsState = {
    data: {
        topics: {},
        rejectedTopicIds: [],
    },
};

export const topicsSlice = createSlice({
    name: "favoriteTemplates",
    initialState,
    reducers: {
        addRejectedTopicIds(state, { payload }: { payload: Array<string> }) {
            if (payload.length > 0) {
                state.data.rejectedTopicIds = payload;
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchTopics.fulfilled, (state, { payload }) => {
                if (payload) {
                    const topics = normalize(payload, normalizedTopics).entities
                        .listedTopics;
                    if (topics) {
                        state.data.topics = topics;
                    }
                }
            })
            .addCase(fetchTopicsByName.fulfilled, (state, { payload }) => {
                if (payload) {
                    const topics = normalize(payload, normalizedTopics).entities
                        .listedTopics;
                    // update the topics with the searched topics
                    // if the topic already exists, update it
                    // if the topic does not exist, add it
                    Object.entries(topics ?? {}).forEach(([id, topic]) => {
                        state.data.topics[id] = topic;
                    });
                }
            })
            .addMatcher(
                (action) => action.type.match(".*rejected$"),
                () => {
                    console.log("action failed.");
                }
            );
    },
});

export const topicSliceActions = topicsSlice.actions;
export const selectExcludedTopicNames = (
    state: RootState,
    deletedTopicPlaceholder: string,
    topicIds?: string[]
) => {
    const topics = state.topics.data.topics;
    return topicIds
        ?.map((id) => topics[id]?.name ?? deletedTopicPlaceholder)
        .join(", ");
};

export const selectAllPublishedTopics = (state: RootState) => {
    return Object.values(state.topics.data.topics)
        .filter((topic) => topic.published)
        .map(({ id, name }) => ({ id, name }));
};

export const selectUnpublishedTopics = (
    state: RootState,
    topicIds: string[]
) => {
    const topics = state.topics.data.topics;
    const topicList = topicIds?.map((id) => {
        return {
            id: id,
            published: topics[id]?.published,
            name: topics[id]?.name,
        };
    });
    return topicList?.filter((topic) => !topic.published);
};

export const selectIsAnyTopicRejected = (
    state: RootState,
    topicIds: string[]
) => {
    const invalidTopics = state.topics.data.rejectedTopicIds;
    return topicIds.some((topicId) => invalidTopics.includes(topicId)) ?? false;
};

export default topicsSlice.reducer;
